import React, { PureComponent, createRef } from "react";
import PropTypes from "prop-types";
import { fieldValidation as validation } from "../utils/field-validation";
import fieldPropTypes from "./field-prop-types";
import ValidationMessage from "../theme/default/validation-message";

const optionIsCheckedState = option => option.name !== "N";

const FormRow = ({
  options,
  name,
  label,
  showRemoveButton,
  onRemove,
  showRelationshipSelect,
  relationship,
  onRelationshipChange,
  value,
  onChange,
  invalid,
  components: { Label, Select, TextField, Text },
  settings
}) => {
  const labelContents = (
    <p className='cell-with-check-multiple-label-web-sdk'>
      <span>{label}</span>
      {showRemoveButton && (
        <Text label={settings.translations.remove} onClick={onRemove} />
      )}
    </p>
  );
  return (
    <div className='cell-with-check-multiple-form-row-web-sdk'>
      {showRelationshipSelect && (
        <div className='cell-with-check-multiple-form-row-cell-web-sdk'>
          <Label text={settings.translations.relationship} />
          <Select
            name={name}
            options={options}
            selectedOption={relationship}
            onChange={onRelationshipChange}
            invalid={!relationship && invalid}
          />
        </div>
      )}
      <div className='cell-with-check-multiple-form-row-cell-web-sdk'>
        <Label text={labelContents} />
        <TextField
          updateValue={onChange}
          fieldAttributes={{ defaultValue: value }}
          invalid={!value && invalid}
        />
      </div>
    </div>
  );
};

export default class CellWithCheckMultiple extends PureComponent {
  constructor(props) {
    super(props);
    this.wrapperRef = createRef();
    const { field, appData } = props;
    validation[field.type].update(field, appData, {
      fieldRef: this.wrapperRef
    });
  }

  componentDidUpdate() {
    const { field, appData } = this.props;
    validation[field.type].update(field, appData, {
      fieldRef: this.wrapperRef
    });
  }

  getValue(option) {
    const {
      field: { name }
    } = this.props;
    return {
      [name]: option.value
    };
  }

  handleCheckboxChange = () => {
    const {
      field: { options },
      appData: { handleChange, values }
    } = this.props;

    const unselectedOption = options.find(o => !this.optionIsSelected(o));
    const newValue = this.getValue(unselectedOption);
    if (this.isFamilyHistory() && !values[this.textName()]) {
      newValue[this.textName()] = "/";
    }
    handleChange(newValue);
  };

  handleAddButtonClick = () => {
    const {
      appData: { values, handleChange }
    } = this.props;
    handleChange({
      [this.textName()]: `${values[this.textName()]}|${
        this.isFamilyHistory() ? "/" : ""
      }`
    });
  };

  handleRemoveButtonClick = index => {
    const {
      appData: { values, handleChange }
    } = this.props;
    const textParts = (values[this.textName()] || "").split("|");
    textParts.splice(index, 1);
    handleChange({ [this.textName()]: textParts.join("|") });
  };

  handleRelationshipSelectChange = (index, value) => {
    const {
      appData: { values, handleChange }
    } = this.props;
    const textParts = (values[this.textName()] || "").split("|");
    textParts[index] = `${textParts[index].split("/")[0]}/${value}`;
    handleChange({ [this.textName()]: textParts.join("|") });
  };

  handleTextFieldChange = (index, e) => {
    e.preventDefault();
    const {
      appData: { values, handleChange }
    } = this.props;
    const textParts = (values[this.textName()] || "").split("|");
    if (this.isFamilyHistory()) {
      textParts[index] = `${e.target.value}/${textParts[index].split("/")[1]}`;
    } else {
      textParts[index] = e.target.value;
    }
    handleChange({ [this.textName()]: textParts.join("|") });
  };

  optionIsSelected(option) {
    const {
      field: { name },
      appData: { values }
    } = this.props;
    return option.value === values[name];
  }

  isFamilyHistory() {
    return this.props.field.params.includes("TDFieldOptionIsFamilyHistory");
  }

  textName(name) {
    return (name ?? this.props.field.name).replace("_id", "");
  }

  render() {
    const {
      field: { options, title, placeholder, name },
      components,
      appData: { values, fieldValidation, settings }
    } = this.props;
    const { Text, CellWithCheck } = components;
    if (options.length < 2) {
      return null;
    }
    const selectedOption = options.find(o => this.optionIsSelected(o));
    const checked = selectedOption && optionIsCheckedState(selectedOption);
    const innerOptions = options.map(o => ({ value: o.text, label: o.text }));
    const rowTexts = (values[this.textName()] || "").split("|");
    const invalid =
      fieldValidation[this.textName()] &&
      !fieldValidation[this.textName()].isValid;
    const { errorMessage } = fieldValidation[this.textName()] || {};
    return (
      <div className='field-wrapper-web-sdk' ref={this.wrapperRef}>
        <CellWithCheck
          name={name}
          label={title}
          handleControlCheck={this.handleCheckboxChange}
          checked={checked}
        />
        {checked && (
          <div>
            {rowTexts.map((rowText, index) => {
              const [value, relationship] = rowText.split("/");
              return (
                <FormRow
                  key={`${name}-${index}`}
                  name={`${name}-${index}`}
                  options={innerOptions}
                  label={placeholder}
                  showRemoveButton={rowTexts.length >= 2}
                  onRemove={() => this.handleRemoveButtonClick(index)}
                  showRelationshipSelect={this.isFamilyHistory()}
                  relationship={relationship}
                  onRelationshipChange={value =>
                    this.handleRelationshipSelectChange(index, value)
                  }
                  value={value}
                  onChange={e => this.handleTextFieldChange(index, e)}
                  invalid={invalid}
                  components={components}
                  settings={settings}
                />
              );
            })}
            <Text
              label={settings.translations.add_new}
              onClick={this.handleAddButtonClick}
            />
          </div>
        )}
        <ValidationMessage isInvalid={invalid}>
          {errorMessage}
        </ValidationMessage>
      </div>
    );
  }
}

FormRow.propTypes = {
  options: PropTypes.arrayOf(PropTypes.object),
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  showRemoveButton: PropTypes.bool.isRequired,
  onRemove: PropTypes.func.isRequired,
  showRelationshipSelect: PropTypes.func.isRequired,
  relationship: PropTypes.string.isRequired,
  onRelationshipChange: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  invalid: PropTypes.bool.isRequired,
  components: PropTypes.objectOf(PropTypes.func),
  settings: PropTypes.objectOf(PropTypes.string)
};
CellWithCheckMultiple.propTypes = fieldPropTypes;
